import type { Branding } from '@logto/schemas';
import { Theme } from '@logto/schemas';
import type { Nullable } from '@silverhand/essentials';

export type GetLogoUrl = {
  theme: Theme;
  logoUrl: string;
  darkLogoUrl?: Nullable<string>;
};

export const getLogoUrl = ({ theme, logoUrl, darkLogoUrl }: GetLogoUrl) => {
  if (theme === Theme.Dark) {
    return darkLogoUrl ?? logoUrl;
  }

  return logoUrl;
};

/**
 * True when dark mode is showing the light-theme logo because no dark one was uploaded. Such a
 * logo is usually dark ink, nearly invisible on the dark card, so it gets a light backdrop.
 */
export const isLightLogoInDark = (
  theme: Theme,
  branding: Branding | undefined,
  logo: Nullable<string> | undefined
) => theme === Theme.Dark && Boolean(logo) && logo === branding?.logoUrl && !branding?.darkLogoUrl;

/** A light chip behind a light-theme logo in dark mode; see `isLightLogoInDark`. */
export const lightLogoBackdropClass = 'rounded-lg bg-white p-1.5';

export type GetBrandingLogoUrl = {
  theme: Theme;
  branding: Branding;
  isDarkModeEnabled: boolean;
};

export const getBrandingLogoUrl = ({ theme, branding, isDarkModeEnabled }: GetBrandingLogoUrl) => {
  const { logoUrl, darkLogoUrl } = branding;

  if (!isDarkModeEnabled) {
    return logoUrl;
  }

  if (!logoUrl && !darkLogoUrl) {
    return null;
  }

  if (logoUrl && darkLogoUrl) {
    return getLogoUrl({ theme, logoUrl, darkLogoUrl });
  }

  return logoUrl ?? darkLogoUrl;
};
